// Shared display-currency helpers.
// The active base currency is synced from the company profile (useCompany)
// into this module so every list/report/PDF formats amounts consistently.

let BASE = 'USD';
try {
  const saved = typeof localStorage !== 'undefined' && localStorage.getItem('ledgerpro_base_currency');
  if (saved) BASE = saved;
} catch (e) { /* SSR / privacy mode */ }

export function setDisplayCurrency(code) {
  if (!code || typeof code !== 'string') return;
  const up = code.toUpperCase();
  if (!/^[A-Z]{3}$/.test(up)) return;
  BASE = up;
  try { localStorage.setItem('ledgerpro_base_currency', up); } catch (e) { /* ignore */ }
}

export function getDisplayCurrency() {
  return BASE;
}

function makeFormatter(options) {
  return (v) => {
    const n = v === null || v === undefined || isNaN(Number(v)) ? 0 : Number(v);
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: BASE, ...options }).format(n);
    } catch (e) {
      // Unknown code (e.g. non-ISO): fall back to symbol-less formatting
      return `${BASE} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };
}

/**
 * Build a money formatter for a specific currency code (e.g. a document's
 * selected currency). Falls back to the company base currency when the code
 * is missing or invalid.
 */
export function makeMoneyFormatter(currency, options = {}) {
  const code = currency && /^[A-Z]{3}$/.test(String(currency).toUpperCase())
    ? String(currency).toUpperCase()
    : BASE;
  return (v) => {
    const n = v === null || v === undefined || isNaN(Number(v)) ? 0 : Number(v);
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, ...options }).format(n);
    } catch (e) {
      return `${code} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };
}

export const fmtMoney = makeFormatter({});
export const fmtMoney0 = makeFormatter({ minimumFractionDigits: 0, maximumFractionDigits: 0 });
export default fmtMoney;

// ---------------------------------------------------------------------------
// Foreign-exchange helpers.
// A document in a foreign currency (e.g. USD) carries an exchange_rate that
// says how many base-currency units one document-currency unit equals
// (1 USD = 70 AFN). We display both the amount in the document currency and
// its base-currency equivalent ("100.00 USD = 7,000.00 AFN").
// ---------------------------------------------------------------------------

// Fallback registry used when the backend currency list is not loaded yet.
export const CURRENCY_REGISTRY = [
  { code: 'USD', symbol: '$',         name: 'US Dollar' },
  { code: 'AFN', symbol: 'AFN',       name: 'Afghan Afghani' },
  { code: 'EUR', symbol: '€',         name: 'Euro' },
  { code: 'GBP', symbol: '£',         name: 'British Pound' },
  { code: 'CAD', symbol: 'CA$',       name: 'Canadian Dollar' },
];

export function currencyName(code) {
  const up = String(code || '').toUpperCase();
  const c = CURRENCY_REGISTRY.find(x => x.code === up);
  return c ? c.name : up;
}

export function currencySymbol(code) {
  const up = String(code || '').toUpperCase();
  const c = CURRENCY_REGISTRY.find(x => x.code === up);
  if (c) return c.symbol;
  return up === 'AFN' ? 'AFN' : up;
}

export function validCurrencyCode(code) {
  return /^[A-Z]{3}$/.test(String(code || '').toUpperCase());
}

/**
 * Convert an amount using a per-document exchange rate.
 * amount * rate where rate = base-currency units per 1 document currency.
 */
export function convertAmount(amount, rate) {
  const a = amount === null || amount === undefined || isNaN(Number(amount)) ? 0 : Number(amount);
  const r = rate === null || rate === undefined || isNaN(Number(rate)) ? 1 : Number(rate);
  return Math.round(a * r * 100) / 100;
}

/**
 * Format an amount in a specific currency. Currency-aware variant that always
 * uses an explicit code (unlike makeMoneyFormatter, which falls back to BASE).
 */
export function fmtCurrency(amount, code) {
  const c = validCurrencyCode(code) ? String(code).toUpperCase() : BASE;
  return makeMoneyFormatter(c)(amount);
}

/**
 * Display the base-currency equivalent of a doc-currency amount.
 * e.g. fmtFx(100, 'USD', 70, 'AFN') -> "$100.00" and the converted value.
 */
export function toBase(amount, exchangeRate, baseCurrency) {
  const base = validCurrencyCode(baseCurrency) ? String(baseCurrency).toUpperCase() : BASE;
  const rate = exchangeRate === undefined || exchangeRate === null ? 1 : Number(exchangeRate);
  return convertAmount(amount, rate);
}

/**
 * Render "1 USD = 70 AFN" for the document header.
 */
export function rateLabel(currency, rate, baseCurrency) {
  const c = validCurrencyCode(currency) ? String(currency).toUpperCase() : BASE;
  const base = validCurrencyCode(baseCurrency) ? String(baseCurrency).toUpperCase() : BASE;
  if (c === base) return null;
  const r = rate === undefined || rate === null ? 1 : Number(rate);
  return `1 ${c} = ${fmtCurrency(r, base)}`;
}

/**
 * Render "100.00 USD = 7,000.00 AFN" — the core exchange display used across
 * the app wherever a transaction is recorded or shown.
 * Passing doc currency === base currency returns the plain single-currency
 * amount (no conversion line).
 */
export function fxLine(amount, currency, exchangeRate, baseCurrency) {
  const c = validCurrencyCode(currency) ? String(currency).toUpperCase() : BASE;
  const base = validCurrencyCode(baseCurrency) ? String(baseCurrency).toUpperCase() : BASE;
  if (c === base) return fmtCurrency(amount, c);
  const rate = exchangeRate === undefined || exchangeRate === null ? 1 : Number(exchangeRate);
  const converted = convertAmount(amount, rate);
  return `${fmtCurrency(amount, c)} = ${fmtCurrency(converted, base)}`;
}

/**
 * React-renderable FX line markup for summary boxes/PDFs: the amount in
 * document currency with the base equivalent beside it.
 */
export function fxSummary(total, currency, exchangeRate, baseCurrency) {
  const c = validCurrencyCode(currency) ? String(currency).toUpperCase() : BASE;
  const base = validCurrencyCode(baseCurrency) ? String(baseCurrency).toUpperCase() : BASE;
  if (c === base) return null;
  const rate = exchangeRate === undefined || exchangeRate === null ? 1 : Number(exchangeRate);
  const converted = convertAmount(total, rate);
  return { currency: c, base, amount: total, converted, rate };
}

function num(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

/**
 * Split a stored transaction (payment, invoice, bill, ...) into the amount in
 * the currency it was actually recorded in plus its base-currency equivalent.
 *
 * This is what makes a USD entry display as "$440.00 = AFN 28,380.00" instead
 * of relabelling 440 as Afghani. `row` may be any API record; the fields read
 * are `currency`, `amount`, `exchange_rate` and `base_amount`.
 *
 * `fallbackRate` is used when the row has no usable rate of its own (e.g. an
 * older payment saved before the rate was stored).
 */
export function docAmount(row, baseCurrency, fallbackRate) {
  const base = validCurrencyCode(baseCurrency) ? String(baseCurrency).toUpperCase() : BASE;
  const currency = validCurrencyCode(row && row.currency) ? String(row.currency).toUpperCase() : base;
  const isForeign = currency !== base;
  const amount = num(row && row.amount);

  const hasBase = row && row.base_amount !== null && row.base_amount !== undefined && row.base_amount !== '';
  const baseAmount = hasBase ? num(row.base_amount) : null;

  let rate = num(row && row.exchange_rate);
  if (!isForeign) {
    rate = 1;
  } else if (!(rate > 0) || rate === 1) {
    // exchange_rate of 1 on a foreign pair is the editors' "unset" placeholder.
    // Prefer the stored base_amount, then the caller-supplied rate, then 1.
    if (baseAmount !== null && amount > 0) rate = baseAmount / amount;
    else if (num(fallbackRate) > 0) rate = num(fallbackRate);
    else rate = 1;
  }

  return {
    currency,
    base,
    rate,
    isForeign,
    amount,
    baseAmount: baseAmount !== null ? baseAmount : convertAmount(amount, rate),
  };
}

